import { ActivatedRouteSnapshot } from '@angular/router';
import { getCompanyCodeFromRoute } from 'src/util/helpers/url-helper';
import { Observable, take } from 'rxjs';
import { TcsPermission } from 'src/app/shared/authorization/permissions';
import { UserViewModel } from 'src/models/person-view-model';
import { firstValueOrDefault } from 'src/util/rxjs';
import { uniq } from 'lodash-es';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { OpsAuthorizationService } from './ops-authorization.service';
import { OrgAuthorizationService } from './org-authorization.service';
import { InsightsAuthorizationService } from './insights-authorization.service';
import { OosAuthorizationService } from './oos-authorization.service';

export type AuthorizationStrategy =
  | 'ops'
  | 'org'
  | 'insights'
  | 'fleet-status'
  | 'oos-rum'
  | 'asb'
  | 'rtt'
  | 'support';

export interface RouteAuthorizationStrategy {
  readonly name: AuthorizationStrategy;
  isAuthorized(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<boolean>;
}

export enum RouteAuthorizationStatus {
  NoRolesGranted = 'noRolesGranted',
  NoAccessGranted = 'noAccessGranted',
  Authorized = 'authorized',
  Unauthorized = 'unauthorized',
}

export interface CanActivateChildPermissions {
  grantingPermissionsDeep: TcsPermission[];
}

export interface CanActivatePermissions {
  grantingPermissions: TcsPermission[];
}

export type RoutePermissionsData = Partial<CanActivateChildPermissions & CanActivatePermissions>;

export function getAuthorizationStrategies(route: ActivatedRouteSnapshot): AuthorizationStrategy[] {
  return uniq(
    route.pathFromRoot
      .map((_) => _.data.authorizationStrategy as AuthorizationStrategy | undefined)
      .filter((as) => as != null) as AuthorizationStrategy[],
  );
}

export function collectAuthorizations(destinationRoute: ActivatedRouteSnapshot): TcsPermission[] {
  const permissions: TcsPermission[] = [];
  const { grantingPermissions } = destinationRoute.data as RoutePermissionsData;
  if (grantingPermissions != null) {
    permissions.push(...grantingPermissions);
  }

  let routeOrParent: ActivatedRouteSnapshot | null = destinationRoute;
  while (routeOrParent != null) {
    const { grantingPermissionsDeep } = routeOrParent.data as RoutePermissionsData;
    if (grantingPermissionsDeep != null) {
      permissions.push(...grantingPermissionsDeep);
    }
    routeOrParent = routeOrParent.parent;
  }
  return uniq(permissions);
}

async function isTrue(obs: Observable<boolean>): Promise<boolean> {
  return await firstValueOrDefault(obs.pipe(take(1)), false);
}

export class OpsAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'ops';
  constructor(private _opsAuthz: OpsAuthorizationService) {}

  async isAuthorized(route: ActivatedRouteSnapshot): Promise<boolean> {
    const companyCode = getCompanyCodeFromRoute(route);
    return companyCode == null
      ? false
      : await isTrue(this._opsAuthz.isAuthorizedForRoute(route, companyCode));
  }
}

export class OrgAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'org';
  constructor(private _orgAuthz: OrgAuthorizationService) {}

  async isAuthorized(route: ActivatedRouteSnapshot): Promise<boolean> {
    const companyCode = getCompanyCodeFromRoute(route);
    return companyCode == null
      ? false
      : await isTrue(this._orgAuthz.isAuthorizedForCompanyCode(companyCode));
  }
}

export class InsightsAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'insights';
  constructor(private _insightsAuthz: InsightsAuthorizationService) {}

  async isAuthorized(route: ActivatedRouteSnapshot): Promise<boolean> {
    const companyCode = getCompanyCodeFromRoute(route);
    return companyCode == null
      ? false
      : await isTrue(this._insightsAuthz.isAuthorizedForCompanyCode(companyCode));
  }
}

export class OosRumAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'oos-rum';
  constructor(private _oosAuthz: OosAuthorizationService) {}

  async isAuthorized(route: ActivatedRouteSnapshot): Promise<boolean> {
    const companyCode = getCompanyCodeFromRoute(route);
    return companyCode == null
      ? false
      : await isTrue(this._oosAuthz.isAuthorizedForRoute(route, companyCode));
  }
}

export class AsbAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'asb';
  constructor(private _permissions: PermissionsService) {}

  async isAuthorized(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<boolean> {
    const roles = user.userRolesAllScopes ?? [];
    return this._permissions.hasAnyPermission(roles, permissions);
  }
}

export class FleetStatusAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'fleet-status';
  constructor(private _permissions: PermissionsService) {}

  async isAuthorized(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<boolean> {
    const roles = user.userRolesAllScopes ?? [];
    return this._permissions.hasAnyPermission(roles, permissions);
  }
}

export class RttAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'rtt';
  constructor(private _permissions: PermissionsService) {}

  async isAuthorized(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<boolean> {
    const roles = user.userRolesAllScopes ?? [];
    return this._permissions.hasAnyPermission(roles, permissions);
  }
}

export class SupportAuthorizationStrategy implements RouteAuthorizationStrategy {
  public readonly name: AuthorizationStrategy = 'support';
  constructor(private _permissions: PermissionsService) {}

  async isAuthorized(
    route: ActivatedRouteSnapshot,
    user: UserViewModel,
    permissions: TcsPermission[],
  ): Promise<boolean> {
    const roles = user.userRolesAllScopes ?? [];
    return this._permissions.hasAnyPermission(roles, permissions);
  }
}
